import React, { useState, useEffect } from "react";
import "../styles/AuthModal.css";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

const AuthModal = ({ 
  show, 
  onHide, 
  onLoginSuccess,
  isLoginMode = false
}) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(isLoginMode);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!show) {
      setFormData({
        full_name: "",
        email: "",
        mobile: "",
        password: "",
      });
      setError("");
      setLoading(false);
    }
  }, [show]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Login failed");
        toast.error(data.error || "Login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      window.dispatchEvent(new Event('authChange'));

      toast.success("Login successful!");

      if (onLoginSuccess) {
        onLoginSuccess();
      }

      onHide();
      navigate("/dashboard");

    } catch (err) {
      console.error("LOGIN ERROR:", err);
      setError("Server connection failed");
      toast.error("Server connection failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.mobile || !formData.password) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const email = formData.email;
    const emailErrors = [];
    if (!email.includes("@")) {
      emailErrors.push("• Must contain an '@' symbol");
    }
    if (!email.includes(".")) {
      emailErrors.push("• Must contain a domain (e.g., .com, .in)");
    }
    const strictEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.includes("@") && email.includes(".") && !strictEmailRegex.test(email)) {
      emailErrors.push("• Must be a properly formatted email address (e.g., name@domain.com)");
    }

    if (emailErrors.length > 0) {
      toast.error(
        <div>
          <span style={{ fontWeight: "400" }}>Email does not meet requirements:</span>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: "4px", listStyle: "none", fontSize: "13px" }}>
            {emailErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>,
        { autoClose: 6000 }
      );
    }

    const pwd = formData.password;
    const pwdErrors = [];
    if (pwd.length < 8)         pwdErrors.push("• At least 8 characters long");
    if (!/[A-Z]/.test(pwd))    pwdErrors.push("• At least one uppercase letter (A-Z)");
    if (!/[a-z]/.test(pwd))    pwdErrors.push("• At least one lowercase letter (a-z)");
    if (!/[0-9]/.test(pwd))    pwdErrors.push("• At least one number (0-9)");
    if (!/[^\w\s]/.test(pwd))  pwdErrors.push("• At least one special character (e.g. @, #, $, !)");
    if (pwdErrors.length > 0) {
      toast.error(
        <div>
          <span style={{ fontWeight: "400" }}>Password does not meet requirements:</span>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: "4px", listStyle: "none", fontSize: "13px" }}>
            {pwdErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>,
        { autoClose: 6000 }
      );
    }

    if (emailErrors.length > 0 || pwdErrors.length > 0) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/api/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Registration failed");
        toast.error(data.error || "Registration failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      window.dispatchEvent(new Event('authChange'));

      toast.success("Account created successfully!");

      if (onLoginSuccess) {
        onLoginSuccess();
      }

      onHide();
      navigate("/dashboard");

    } catch (err) {
      console.error("REGISTER ERROR:", err);
      setError("Server connection failed");
      toast.error("Server connection failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Updated: Close modal and navigate to login page
  const toggleMode = () => {
    // Close the modal
    onHide();
    // Navigate to login page
    navigate("/login");
  };

  if (!show) return null;

  return (
    <div className="auth-overlay" onClick={onHide}>
      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
      `}</style>
      
      <div className="auth-backdrop" onClick={onHide}></div>

      <div
        className="container auth-container p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row g-0 h-100">
          <div className="col-md-8 auth-left d-flex flex-column">
            <div className="logo mb-4">
              <img src="/images/logs Home.svg" alt="" />
            </div>

            <div className="feature gap-2">
              <img src="/images/1.svg" alt="" />
              <span>Tender & Bidding Document Preparation</span>
            </div>

            <div className="feature gap-2">
              <img src="/images/2.svg" alt="" />
              <span>Timely Alerts & Notifications</span>
            </div>

            <div className="feature gap-2">
              <img src="/images/3.svg" alt="" />
              <span>Past Tenders & Historic Data Analysis</span>
            </div>

            <div className="mt-auto text-center">
              <img
                src="/images/SMT logs.svg"
                alt="illustration"
                className="auth-image"
              />
            </div>
          </div>

          <div className="col-md-4 auth-right">
            <button
              onClick={onHide}
              style={{
                position: "absolute",
                top: "10px",
                right: "15px",
                border: "none",
                background: "transparent",
                fontSize: "20px",
                cursor: "pointer",
                zIndex: 10,
              }}
            >
              ✕
            </button>

            <h2 className="mb-4">{isLogin ? "Login" : "Register Now"}</h2>

            <form onSubmit={isLogin ? handleLogin : handleRegister} autoComplete="off">
              {!isLogin && (
                <div className="mb-3">
                  <label className="form-label">Full Name*</label>
                  <input
                    className="form-control"
                    placeholder="Enter Name"
                    required
                    autoComplete="off"
                    value={formData.full_name}
                    onChange={(e) =>
                      setFormData({ ...formData, full_name: e.target.value })
                    }
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Email*</label>
                <input
                  className="form-control"
                  placeholder="Enter Mail"
                  type="email"
                  required
                  autoComplete="off"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              {!isLogin && (
                <div className="mb-3">
                  <label className="form-label">Mobile*</label>
                  <PhoneInput
                    country={"in"}
                    enableSearch={true}
                    value={formData.mobile}
                    onChange={(phone) =>
                      setFormData({
                        ...formData,
                        mobile: `+${phone}`
                      })
                    }
                    inputStyle={{
                      width: "100%",
                      height: "38px"
                    }}
                    containerStyle={{
                      width: "100%"
                    }}
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Password*</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Enter Password"
                    required
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    style={{ paddingRight: "40px" }}
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "12px",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#2F7A87"
                    }}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </span>
                </div>
              </div>

              <button
                type="submit"
                className="btn auth-btn w-100 no-hover text-center"
                style={{ 
                  backgroundColor: "#2F7F8F", 
                  borderRadius: "20px", 
                  color: "white", 
                  borderColor: "#2F7F8F",
                  padding: "10px"
                }}
                disabled={loading}
              >
                {loading 
                  ? (isLogin ? "Logging in..." : "Registering...") 
                  : (isLogin ? "Login" : "Register and Login")}
              </button>
            </form>

            {/* ✅ Updated: This link now closes modal and navigates to login page */}
            <p className="login-text mt-3">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                className="login-link"
                onClick={toggleMode}
                style={{
                  background: "none",
                  border: "none",
                  color: "#2F7F8F",
                  textDecoration: "underline",
                  cursor: "pointer"
                }}
              >
                {isLogin ? "Register" : "Login"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;